import { FC, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Formik, Form, FormikProps } from 'formik';
import * as Yup from 'yup';
import EntityForm from './components/EntityForm';
import { AddEntityPageProps, NewEntityFormValues, OptionType } from './models/interfaces';
import { isMobile } from 'react-device-detect';
import { addNewPhysician } from '../api/api';
import { mapEntityToApiData } from './utils/mapEntityToApiData';
import {
    normalSuccessToast,
    showErrorToast,
} from '../../components/commonfunction';
import { fetchRecordsetData } from './utils/signatureApi';
import { getDropDownDataFromApi } from '../../components/commonfunctionTS';

const AddEntityPage: FC<AddEntityPageProps> = ({
    setModel,
    headerColumns,
    physicianRecordSetId,
    embeddedInModal = false,
    initialFirstName = '',
    initialLookupKeyType = '',
    onEntityCreated,
    submitButtonLabel = 'Add Physician',
    successToastMessage = 'Physician created successfully',
    formTitle = 'Add Entity',
}) => {
    const [lookupOptions, setLookupOptions] = useState<OptionType[]>([]);
    const [welcomeSenderOptions, setWelcomeSenderOptions] = useState<
        OptionType[]
    >([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const formikRef = useRef<FormikProps<any>>(null);

    useEffect(() => {
        const lookupKeyField = headerColumns.find(
            (col: any) => col.name === 'Lookup Key Type'
        )?.fieldId;
        if (lookupKeyField) {
            fetchDropDownData(lookupKeyField);
        }

        fetchRecordsetData('Send Entity Email')
            .then((sendEntityEmails) => {
                if (sendEntityEmails) {
                    const { data } = sendEntityEmails;
                    if (data) {
                        const response: OptionType[] =
                            data.dataTable.dataRows.map((item: any) => ({
                                value: item.id,
                                label: item.values,
                            }));
                        setWelcomeSenderOptions(response);
                    }
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }, [headerColumns]);

    const fetchDropDownData = async (lookupKeyField: any) => {
        const data = await getDropDownDataFromApi(lookupKeyField);
        if (data.values) {
            const response: OptionType[] = data.values.map((item: string) => ({
                value: item ?? '',
                label: item ?? '',
            }));
            setLookupOptions(response);
        }
    };

    const resolveLookupKeyTypeValue = (
        options: OptionType[],
        preferred: string
    ) => {
        if (!preferred) {
            return '';
        }
        const normalized = preferred.trim().toLowerCase();
        const hit = options.find(
            (option) =>
                String(option.value).trim().toLowerCase() === normalized ||
                String(option.label).trim().toLowerCase() === normalized
        );
        return hit?.value ?? preferred;
    };

    useEffect(() => {
        const preferred =
            initialLookupKeyType || (embeddedInModal ? 'Signer Email' : '');
        if (!preferred || lookupOptions.length === 0) {
            return;
        }
        const resolved = resolveLookupKeyTypeValue(lookupOptions, preferred);
        formikRef.current?.setFieldValue('lookupKeyType', resolved);
        const email = formikRef.current?.values?.communicationEmail;
        if (
            email &&
            String(resolved).trim().toLowerCase() === 'signer email'
        ) {
            formikRef.current?.setFieldValue('lookupKeyValue', email);
        }
    }, [lookupOptions, initialLookupKeyType, embeddedInModal]);

    const createPhysician = async (values: object) => {
        setIsSubmitting(true);
        try {
            const apiData = mapEntityToApiData(values, headerColumns);
            const response = await addNewPhysician(
                apiData,
                physicianRecordSetId
            );

            if (
                !response ||
                response.status === 400 ||
                response.status === 404
            ) {
                const responseData = await response?.json();
                if (
                    responseData.message.includes(
                        'Communication Email is already in use'
                    )
                ) {
                    showErrorToast(
                        'Entity with this Communication Email already exists'
                    );
                } else {
                    showErrorToast('Failed to create physician');
                }
            } else {
                onEntityCreated?.(values as NewEntityFormValues);
                setModel(false);
                normalSuccessToast(successToastMessage);
            }
        } catch (error) {
            showErrorToast('Failed to create physician');
        } finally {
            setIsSubmitting(false);
        }
    };

    const defaultLookupKeyType =
        initialLookupKeyType || (embeddedInModal ? 'Signer Email' : '');

    const formInitialValues = {
        ...initialValues,
        firstName: initialFirstName || '',
        lookupKeyType: defaultLookupKeyType,
    };

    const formContent = (
        <>
            <div className={embeddedInModal ? 'listing-grid px-4 pt-3' : 'listing-grid'}>
                <Formik
                    innerRef={formikRef}
                    onSubmit={createPhysician}
                    initialValues={formInitialValues}
                    validationSchema={validationSchema}
                    enableReinitialize
                >
                    {(formik) => (
                        <Form>
                            <EntityForm
                                formik={formik}
                                lookupOptions={lookupOptions}
                                welcomeSenderOptions={welcomeSenderOptions}
                                title={formTitle}
                                syncCommunicationEmailToLookupValue={
                                    embeddedInModal
                                }
                            />
                        </Form>
                    )}
                </Formik>
            </div>

            <div>
                {!embeddedInModal ? <hr /> : null}
                <div
                    className={
                        embeddedInModal
                            ? 'd-flex footer-btn align-items-stretch justify-content-between px-4 py-3 border-top'
                            : 'd-flex footer-btn align-items-stretch justify-content-between mb-2 pl-7 pr-7'
                    }
                >
                    <div className='d-flex'>
                        <Button
                            title='Cancel'
                            variant='secondary'
                            onClick={() => setModel(false)}
                        >
                            Cancel
                        </Button>
                    </div>
                    <div className='d-flex'>
                        <Button
                            title='Add'
                            type='submit'
                            variant='success'
                            disabled={isSubmitting}
                            onClick={() => formikRef?.current?.handleSubmit()}
                        >
                            {isSubmitting ? (
                                <span
                                    className='spinner-border spinner-border-sm'
                                    role='status'
                                    aria-hidden='true'
                                />
                            ) : (
                                <span>{submitButtonLabel}</span>
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );

    if (embeddedInModal) {
        return formContent;
    }

    return (
        <div
            id='right-aside'
            className='right-aside py-7 d-flex flex-column justify-content-between'
            style={
                isMobile
                    ? { minHeight: window.innerHeight - 112 }
                    : { minHeight: window.innerHeight - 158 }
            }
        >
            {formContent}
        </div>
    );
};

export default AddEntityPage;

const initialValues = {
    communicationEmail: '',
    firstName: '',
    lastName: '',
    fullNameWithTitle: '',
    countryCode: '+1',
    smsNumber: '',
    lookupKeyType: '',
    lookupKeyValue: '',
    resendDays: '',
    keepDocumentsFromGroups: false,
    disableAutoResend: false,
    welcomeSender: '',
    changeCounter: 0,
};

const validationSchema = Yup.object().shape({
    communicationEmail: Yup.string()
        .email('Invalid email')
        .required('Email is required'),
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    smsNumber: Yup.string()
        .matches(/^\d{3}-\d{3}-\d{4}$/, 'SMS number must be 10 digits')
        .nullable(true),
});
